import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { BillingStatusBadge } from "../../components/billing/BillingBadges";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { StepUpDialog } from "../../components/StepUpDialog";
import { ApiError, mfaChallenge } from "../../lib/api";
import {
  acceptQuote,
  billingErrorCode,
  deleteQuote,
  getQuote,
  isApiError,
  listAccounts,
  rejectQuote,
  sendQuote,
} from "../../lib/billing-api";
import type { Account, QuoteDetail } from "../../lib/billing-types";
import { formatDate, formatMoney, formatPercent, formatQuantity } from "../../lib/format";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  invalid_state_transition: "billing.error.invalidTransition",
  "billing.error.invalid_state_transition": "billing.error.invalidTransition",
  billing_quote_not_found: "billing.error.notFound",
  billing_item_immutable: "billing.error.itemImmutable",
};

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="detail-list__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function QuoteDetailInner({ id }: { id: string }): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const navigate = useNavigate();

  const canSend = hasPermission("billing.quote.send");
  const canAccept = hasPermission("billing.quote.accept");
  const canReject = hasPermission("billing.quote.reject");
  const canUpdate = hasPermission("billing.quote.update");
  const canDelete = hasPermission("billing.quote.delete");

  const [detail, setDetail] = useState<QuoteDetail | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null);
  const [challengeBusy, setChallengeBusy] = useState(false);
  const [challengeError, setChallengeError] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [quote, accountPage] = await Promise.all([getQuote(id), listAccounts()]);
      setDetail(quote);
      setAccounts(accountPage.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = useCallback(
    async (action: () => Promise<void>): Promise<void> => {
      setActionError(null);
      setBusy(true);
      try {
        await action();
        await load();
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === "mfa_required") {
          setChallengeError(null);
          setPending(() => action);
          return;
        }
        const code = billingErrorCode(caught);
        const key = code === null ? undefined : ERROR_KEYS[code];
        setActionError(
          key !== undefined
            ? t(key)
            : isApiError(caught)
              ? caught.detail
              : t("error.network"),
        );
      } finally {
        setBusy(false);
      }
    },
    [load, t],
  );

  const onChallengeSubmit = async (code: string): Promise<void> => {
    setChallengeBusy(true);
    setChallengeError(null);
    try {
      await mfaChallenge(code);
      const action = pending;
      setPending(null);
      if (action !== null) {
        await runAction(action);
      }
    } catch (caught) {
      setChallengeError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setChallengeBusy(false);
    }
  };

  if (loading) {
    return <Spinner label={t("app.loading")} />;
  }
  if (detail === null) {
    return (
      <section className="page">
        <Alert tone="error">{error ?? t("billing.error.notFound")}</Alert>
        <Link className="btn btn--ghost" to="/billing/quotes">
          {t("billing.quotes.title")}
        </Link>
      </section>
    );
  }

  const status = detail.status;
  const accountName =
    accounts.find((account) => account.id === detail.account_id)?.name ?? detail.account_id;
  const editable = status === "draft";

  return (
    <section className="page" aria-labelledby="billing-quote-detail-title">
      <header className="page__header page__header--row">
        <div>
          <p className="muted">
            <Link to="/billing/quotes">{t("billing.quotes.title")}</Link>
          </p>
          <h1 id="billing-quote-detail-title">{t("billing.quote.detail")}</h1>
          <p className="muted">
            <BillingStatusBadge status={status} />{" "}
            {detail.number === null ? "" : `· #${String(detail.number)}`}
          </p>
        </div>
        <div className="inline-actions">
          {canUpdate && editable ? (
            <Link className="btn btn--ghost" to={`/billing/quotes/${detail.id}/edit`}>
              {t("common.edit")}
            </Link>
          ) : null}
          {canSend && status === "draft" ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() =>
                void runAction(async () => {
                  await sendQuote(detail.id);
                })
              }
            >
              {t("billing.action.send")}
            </button>
          ) : null}
          {canAccept && status === "sent" ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() =>
                void runAction(async () => {
                  await acceptQuote(detail.id);
                })
              }
            >
              {t("billing.action.accept")}
            </button>
          ) : null}
          {canReject && status === "sent" ? (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              onClick={() =>
                void runAction(async () => {
                  await rejectQuote(detail.id);
                })
              }
            >
              {t("billing.action.reject")}
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="btn btn--danger"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
            >
              {t("billing.common.delete")}
            </button>
          ) : null}
        </div>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}

      <dl className="detail-list">
        <Field label={t("billing.quote.account")}>{accountName}</Field>
        <Field label={t("billing.quote.issueDate")}>
          {formatDate(detail.issue_date, locale)}
        </Field>
        <Field label={t("billing.quote.validUntil")}>{formatDate(detail.valid_until, locale)}</Field>
        <Field label={t("billing.common.currency")}>{detail.currency}</Field>
        <Field label={t("billing.quote.notes")}>
          {detail.notes ?? t("billing.common.none")}
        </Field>
        <Field label={t("billing.quote.created")}>
          {formatDate(detail.created_at, locale, true)}
        </Field>
        <Field label={t("billing.quote.sentAt")}>{formatDate(detail.sent_at, locale, true)}</Field>
        <Field label={t("billing.quote.acceptedAt")}>
          {formatDate(detail.accepted_at, locale, true)}
        </Field>
        <Field label={t("billing.quote.rejectedAt")}>
          {formatDate(detail.rejected_at, locale, true)}
        </Field>
      </dl>

      <h2 className="section-title">{t("billing.quote.items")}</h2>
      {detail.items.length === 0 ? (
        <p className="empty">{t("billing.quote.noItems")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.quote.items")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.item.description")}</th>
                <th scope="col">{t("billing.item.quantity")}</th>
                <th scope="col">{t("billing.item.unitPrice")}</th>
                <th scope="col">{t("billing.item.discount")}</th>
                <th scope="col">{t("billing.item.tax")}</th>
                <th scope="col">{t("billing.common.lineTotal")}</th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{formatQuantity(item.quantity_milli, locale)}</td>
                  <td>{formatMoney(item.unit_price_minor, detail.currency, locale)}</td>
                  <td>{formatPercent(item.discount_bp, locale)}</td>
                  <td>{item.tax_name ?? t("billing.item.taxNone")}</td>
                  <td>{formatMoney(item.line_total_minor, detail.currency, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <dl className="detail-list">
        <Field label={t("billing.quote.subtotal")}>
          {formatMoney(detail.subtotal_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.quote.discount")}>
          {formatMoney(detail.discount_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.quote.tax")}>
          {formatMoney(detail.tax_minor, detail.currency, locale)}
        </Field>
        <Field label={t("billing.quote.total")}>
          {formatMoney(detail.total_minor, detail.currency, locale)}
        </Field>
      </dl>

      {confirmDelete ? (
        <Modal
          title={t("billing.action.deleteTitle")}
          onClose={() => setConfirmDelete(false)}
          busy={busy}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() =>
                  void runAction(async () => {
                    await deleteQuote(detail.id);
                    navigate("/billing/quotes");
                  })
                }
              >
                {t("billing.common.delete")}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirmDelete(false)}
              >
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("billing.action.deleteTitle")}</p>
        </Modal>
      ) : null}

      <StepUpDialog
        open={pending !== null}
        busy={challengeBusy}
        error={challengeError}
        onCancel={() => {
          setPending(null);
          setChallengeError(null);
        }}
        onSubmit={(code) => void onChallengeSubmit(code)}
      />
    </section>
  );
}

function QuoteDetailRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { t } = useConfig();
  if (id === undefined) {
    return (
      <section className="page">
        <Alert tone="error">{t("billing.error.notFound")}</Alert>
        <Link className="btn btn--ghost" to="/billing/quotes">
          {t("billing.quotes.title")}
        </Link>
      </section>
    );
  }
  return <QuoteDetailInner id={id} />;
}

export function QuoteDetailPage(): ReactNode {
  return (
    <RequirePermission permission="billing.quote.read">
      <QuoteDetailRoute />
    </RequirePermission>
  );
}
